/**
 * MONTAR PERCURSO — quais itens o aluno vai responder, e em que ordem.
 *
 * À esquerda, o catálogo do banco; à direita, a lista do percurso. Cada item pode
 * entrar mais de uma vez: com variante nova, ele traz outros números e outras figuras a
 * cada vez. Uma variante fixada no laboratório aparece como tal, e pode ser "solta".
 */

import { REGISTRY } from '../engine/registry.ts';
import { adjetivo, conta, lista, numeroDe, verbo } from '../core/pt.ts';
import { getTemplate } from '../engine/registry.ts';
import type { InteractivityLevel } from '../schema/item.ts';
import { COMPETENCY_LABEL } from '../schema/taxonomy.ts';
import {
  carregarConfig,
  codigoDoPercurso,
  configTodos,
  linkDoPercurso,
  montarInstancias,
  percursoDoCodigo,
  EVENTO_MUDOU,
  fixadaDesatualizada,
  novoId,
  ORDEM_TXT,
  salvarConfig,
  type ConfigPercurso,
  type EntradaPercurso,
  type OrdemPercurso,
} from './config.ts';
import { habilidadeDe, NIVEL_TXT } from './itemMeta.ts';
import { naoServemNoPapel, papelDe } from './papel.ts';
import { abrirProvaImpressa } from './prova.ts';
import type { ContextoApp, Vista } from './tipos.ts';

/**
 * O FILTRO DO CATÁLOGO vive fora de `desenhar`: a vista se redesenha inteira a cada
 * item adicionado, e um filtro que se perdesse a cada clique seria inútil justamente
 * para quem está montando uma prova de dez questões.
 */
interface Filtro {
  readonly niveis: ReadonlySet<InteractivityLevel>;
  readonly soPapel: boolean;
}

const NIVEIS: readonly InteractivityLevel[] = ['N0', 'N1', 'N2', 'N3', 'N4'];

export function criarVistaMontar(ctx: ContextoApp): Vista {
  const root = document.createElement('section');
  root.className = 'montar';

  let filtro: Filtro = { niveis: new Set(NIVEIS), soPapel: false };

  function mudar(c: ConfigPercurso): void {
    salvarConfig(c); // dispara EVENTO_MUDOU, que redesenha
  }

  function desenhar(): void {
    const c = carregarConfig();
    const foco = focoAtual();
    root.replaceChildren();

    // --- cabeçalho ----------------------------------------------------------
    const cabeca = document.createElement('header');
    cabeca.className = 'montar-cabeca';
    const h1 = document.createElement('h1');
    h1.textContent = 'Montar percurso';
    const p = document.createElement('p');
    p.textContent =
      'Escolha quais itens entram no percurso e em que ordem. Um item pode entrar mais de ' +
      'uma vez: a cada percurso ele traz números e figuras novos — a não ser que você fixe ' +
      'uma variante em Itens.';
    cabeca.append(h1, p);

    /*
     * FILTRAR PARA MONTAR PROVA DE PAPEL.
     *
     * Duas peneiras, e elas não são a mesma. O nível diz quanto o item interage; "serve
     * no papel" diz se ele sobrevive à impressora — e o item da régua sobre o mapa
     * mostra que as duas não coincidem: ele é N2, como itens que imprimem sem perda
     * nenhuma, mas medir a figura impressa depende da escala da página.
     */
    const filtros = document.createElement('div');
    filtros.className = 'montar-filtros';

    const rotNivel = texto('span', 'Nível de interação');
    rotNivel.className = 'montar-filtro-rotulo';
    filtros.appendChild(rotNivel);

    const chips = document.createElement('div');
    chips.className = 'montar-chips';
    for (const nv of NIVEIS) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'montar-chip';
      b.textContent = nv;
      b.dataset['foco'] = `nivel:${nv}`;
      const ligado = filtro.niveis.has(nv);
      b.setAttribute('aria-pressed', String(ligado));
      b.title = `${NIVEL_TXT[nv]} — clique para mostrar ou esconder`;
      b.addEventListener('click', () => {
        const n = new Set(filtro.niveis);
        if (n.has(nv)) n.delete(nv);
        else n.add(nv);
        // Esconder tudo deixaria o catálogo vazio sem explicação: o último não sai.
        if (n.size === 0) return;
        filtro = { ...filtro, niveis: n };
        desenhar();
      });
      chips.appendChild(b);
    }
    filtros.appendChild(chips);

    const soPapel = document.createElement('label');
    soPapel.className = 'montar-check';
    const cx = document.createElement('input');
    cx.type = 'checkbox';
    cx.checked = filtro.soPapel;
    cx.dataset['foco'] = 'papel';
    cx.addEventListener('change', () => {
      filtro = { ...filtro, soPapel: cx.checked };
      desenhar();
    });
    soPapel.append(cx, document.createTextNode(' Só o que funciona impresso'));
    filtros.appendChild(soPapel);

    cabeca.appendChild(filtros);

    const grade = document.createElement('div');
    grade.className = 'montar-grade';

    // --- catálogo -----------------------------------------------------------
    const cat = painel('Itens disponíveis');
    const cards = document.createElement('div');
    cards.className = 'montar-cards';
    const visiveis = REGISTRY.filter(
      (t) => filtro.niveis.has(t.level) && (!filtro.soPapel || papelDe(t.id as string).serve),
    );
    if (visiveis.length === 0) {
      const vazio = texto('p', 'Nenhum item com esses filtros.');
      vazio.className = 'montar-card-desc';
      cards.appendChild(vazio);
    }
    for (const t of visiveis) {
      const card = document.createElement('article');
      card.className = 'montar-card';

      const topo = document.createElement('div');
      topo.className = 'montar-card-topo';
      topo.append(chipNivel(t.level), texto('span', t.title));

      const hab = habilidadeDe(t);
      const sub = texto(
        'p',
        hab
          ? `${hab.codigo as string} · ${COMPETENCY_LABEL[hab.competencia]}`
          : t.skills.join(', '),
      );
      sub.className = 'montar-card-sub';
      const desc = texto('p', hab?.descricao ?? '');
      desc.className = 'montar-card-desc';

      const acoes = document.createElement('div');
      acoes.className = 'montar-card-acoes';
      const add = botao('+ Adicionar', 'app-btn-mini');
      add.dataset['foco'] = `add:${t.id as string}`;
      add.addEventListener('click', () =>
        mudar({ ...c, entradas: [...c.entradas, { id: novoId(), templateId: t.id as string }] }),
      );
      const ver = botao('Ver em Itens', 'app-link');
      ver.addEventListener('click', () => ctx.abrirNoLaboratorio(t.id as string));
      acoes.append(add, ver);

      const n = c.entradas.filter((e) => e.templateId === (t.id as string)).length;
      if (n > 0) {
        const cont = texto('span', `${n}× no percurso`);
        cont.className = 'montar-contagem';
        acoes.appendChild(cont);
      }

      // O selo só aparece quando há o que dizer: item que imprime sem perda não ganha
      // enfeite nenhum.
      const pp = papelDe(t.id as string);
      if (!pp.serve || pp.ressalva) {
        const selo = texto(
          'p',
          `${pp.serve ? 'No papel' : 'Não imprime'}: ${pp.ressalva ?? pp.motivo}.`,
        );
        selo.className = pp.serve ? 'montar-papel' : 'montar-papel is-nao';
        card.append(topo, sub, desc, selo, acoes);
      } else {
        card.append(topo, sub, desc, acoes);
      }
      cards.appendChild(card);
    }
    cat.appendChild(cards);

    // --- o percurso ---------------------------------------------------------
    const n = c.entradas.length;
    const seu = painel(`Seu percurso · ${conta(n, 'questao')}`);
    seu.classList.add('montar-seu');

    const ordem = document.createElement('label');
    ordem.className = 'montar-ordem';
    const sel = document.createElement('select');
    sel.dataset['foco'] = 'ordem';
    for (const [valor, rotulo] of Object.entries(ORDEM_TXT)) {
      const o = document.createElement('option');
      o.value = valor;
      o.textContent = rotulo;
      o.selected = valor === c.ordem;
      sel.appendChild(o);
    }
    sel.addEventListener('change', () => mudar({ ...c, ordem: sel.value as OrdemPercurso }));
    ordem.append(document.createTextNode('Ordem das questões'), sel);
    seu.appendChild(ordem);

    if (n === 0) {
      const vazio = texto('p', 'Nenhuma questão ainda. Adicione itens pelo catálogo ao lado.');
      vazio.className = 'montar-vazio';
      seu.appendChild(vazio);
    } else {
      const ol = document.createElement('ol');
      ol.className = 'montar-lista';
      c.entradas.forEach((e, i) => ol.appendChild(linhaDe(c, e, i)));
      seu.appendChild(ol);
    }

    const rodape = document.createElement('div');
    rodape.className = 'montar-rodape';
    const todos = botao('Usar todos os itens', 'app-btn-mini');
    todos.dataset['foco'] = 'todos';
    todos.addEventListener('click', () => mudar({ ...configTodos(), ordem: c.ordem }));
    const esvaziar = botao('Esvaziar', 'app-btn-mini');
    esvaziar.dataset['foco'] = 'esvaziar';
    esvaziar.disabled = n === 0;
    esvaziar.addEventListener('click', () => mudar({ ...c, entradas: [] }));
    const ir = botao('Ir para o percurso →', 'app-btn');
    ir.disabled = n === 0;
    ir.addEventListener('click', () => ctx.irPara('percurso'));
    const imprimir = botao('Versão para imprimir', 'app-btn-mini');
    imprimir.disabled = n === 0;
    imprimir.addEventListener('click', () => {
      const insts = montarInstancias(c);
      if (insts.length === 0) {
        ctx.avisar('Não foi possível gerar as questões agora.');
        return;
      }
      abrirProvaImpressa(insts);
    });
    rodape.append(todos, esvaziar, imprimir, ir);

    /*
     * O AVISO DE IMPRESSÃO fica ao lado do botão que imprime, e não no catálogo: é ali
     * que a decisão é tomada. Ele aparece mesmo com o filtro desligado — quem montou o
     * percurso antes de pensar em papel precisa ser avisado, e não procurado.
     */
    const naoImprimem = naoServemNoPapel(c.entradas.map((e) => e.templateId));
    if (naoImprimem.length > 0) {
      const nomes = naoImprimem.map((id) => {
        try {
          return getTemplate(id).title;
        } catch {
          return id;
        }
      });
      const av = texto(
        'p',
        `${conta(naoImprimem.length, 'questao')} do percurso não ${verbo('funcionar.pres', numeroDe(naoImprimem.length))} ${adjetivo('impresso', 'f', numeroDe(naoImprimem.length))}: ${lista(nomes)}.`,
      );
      av.className = 'montar-aviso-papel';
      rodape.appendChild(av);
    }
    seu.appendChild(rodape);
    seu.appendChild(painelCompartilhar(c));

    grade.append(cat, seu);
    root.append(cabeca, grade);
    if (foco) devolverFoco(foco);
  }

  /*
   * O FOCO SOBREVIVE AO REDESENHO. A vista se refaz inteira a cada mudança, e o botão
   * que tinha o foco era destruído junto: ele caía no começo da página, e quem monta o
   * percurso pelo teclado perdia o lugar a cada item acrescentado. Cada botão que muda o
   * percurso leva uma chave (`data-foco`), e o foco volta ao de mesma chave — ou, se ele
   * travou ou sumiu, ao vizinho que faz sentido.
   */
  function focoAtual(): { readonly chave: string; readonly indice: number } | null {
    const el = document.activeElement as HTMLElement | null;
    const chave = el?.dataset?.['foco'];
    if (!el || !chave || !root.contains(el)) return null;
    const tirar = [...root.querySelectorAll('[data-foco^="tirar:"]')];
    return { chave, indice: tirar.indexOf(el) };
  }

  function devolverFoco({ chave, indice }: { chave: string; indice: number }): void {
    const [acao, id] = chave.split(':');
    // No topo da lista o ↑ trava; no fim, o ↓; e "Soltar" some com a variante fixa.
    const candidatos = id ? [chave, `subir:${id}`, `descer:${id}`, `tirar:${id}`] : [chave];
    if (acao === 'esvaziar') candidatos.push('todos');
    for (const k of candidatos) {
      const b = root.querySelector<HTMLButtonElement>(`[data-foco="${k}"]`);
      if (b && !b.disabled) {
        b.focus({ preventScroll: true });
        return;
      }
    }
    // A entrada saiu da lista: o foco vai para a que ocupou o lugar dela.
    if (acao === 'tirar') {
      const tirar = [...root.querySelectorAll<HTMLButtonElement>('[data-foco^="tirar:"]')];
      const vizinho = tirar[Math.min(indice, tirar.length - 1)];
      (vizinho ?? root.querySelector<HTMLButtonElement>('[data-foco="todos"]'))?.focus({
        preventScroll: true,
      });
    }
  }

  /**
   * COMPARTILHAR: o percurso num código (e num link), e o campo para receber um. O
   * código leva os itens, a ordem e as variantes fixadas; quem o recebe tem o mesmo
   * percurso — com variantes novas onde elas não foram fixadas.
   */
  function painelCompartilhar(c: ConfigPercurso): HTMLElement {
    const box = document.createElement('details');
    box.className = 'montar-compartilhar';
    const sum = document.createElement('summary');
    sum.textContent = 'Enviar este percurso aos alunos';
    const exp = texto(
      'p',
      'O link leva este mesmo percurso — os itens, a ordem e as variantes fixadas —, com variantes novas onde ' +
        'elas não foram fixadas. Quem receber o link abre o percurso direto; nada precisa ser instalado.',
    );

    /*
     * O LINK É O CAMINHO PRINCIPAL. O código `P1.…` continua existindo — num ambiente
     * escolar fechado, colar texto sempre funciona e abrir link nem sempre —, mas ele
     * sai da frente: quem manda percurso para uma turma manda um endereço, não uma
     * parede de base64.
     */
    const acoes = document.createElement('div');
    acoes.className = 'montar-compartilhar-acoes';
    const copiarLink = botao('Copiar link', 'app-btn');
    copiarLink.disabled = c.entradas.length === 0;
    copiarLink.addEventListener('click', () => void copiar(linkDoPercurso(c), 'Link copiado.'));
    acoes.append(copiarLink);

    const caixaCodigo = document.createElement('details');
    caixaCodigo.className = 'montar-codigo-caixa';
    const sumCodigo = document.createElement('summary');
    sumCodigo.textContent = 'Ou copiar o código, para onde link não passa';
    const codigo = document.createElement('textarea');
    codigo.className = 'montar-codigo';
    codigo.readOnly = true;
    codigo.rows = 3;
    codigo.setAttribute('aria-label', 'Código deste percurso');
    codigo.value = c.entradas.length > 0 ? codigoDoPercurso(c) : '';
    codigo.addEventListener('focus', () => codigo.select());
    const copiarCodigo = botao('Copiar código', 'app-btn-mini');
    copiarCodigo.disabled = c.entradas.length === 0;
    copiarCodigo.addEventListener('click', () => void copiar(codigo.value, 'Código copiado.'));
    caixaCodigo.append(sumCodigo, codigo, copiarCodigo);

    const rotulo = texto('p', 'Recebeu um código ou um link? Cole aqui:');
    rotulo.className = 'montar-colar-rotulo';
    const linha = document.createElement('div');
    linha.className = 'montar-colar-linha';
    const entrada = document.createElement('input');
    entrada.type = 'text';
    entrada.className = 'montar-colar';
    entrada.placeholder = 'P1.…';
    entrada.setAttribute('aria-label', 'Código de percurso recebido');
    const carregar = botao('Carregar', 'app-btn-mini');
    carregar.addEventListener('click', () => {
      const novo = percursoDoCodigo(entrada.value);
      if (!novo) {
        ctx.avisar('Esse código não traz um percurso válido.');
        return;
      }
      mudar(novo);
      ctx.avisar(`Percurso carregado: ${conta(novo.entradas.length, 'questao')}.`);
    });
    linha.append(entrada, carregar);

    box.append(sum, exp, acoes, caixaCodigo, rotulo, linha);
    return box;
  }

  async function copiar(conteudo: string, ok: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(conteudo);
      ctx.avisar(ok);
    } catch {
      // Página embutida sem permissão de área de transferência: o código está à vista.
      ctx.avisar('Não deu para copiar sozinho: selecione o código e copie com Ctrl+C.');
    }
  }

  function linhaDe(c: ConfigPercurso, e: EntradaPercurso, i: number): HTMLElement {
    const t = getTemplate(e.templateId);
    const li = document.createElement('li');
    li.className = 'montar-entrada';

    const num = texto('span', String(i + 1));
    num.className = 'montar-num';

    const corpo = document.createElement('div');
    const titulo = document.createElement('div');
    titulo.className = 'montar-entrada-titulo';
    titulo.append(chipNivel(t.level), texto('span', t.title));

    const variante = document.createElement('div');
    variante.className = e.seed ? 'montar-entrada-var is-fixa' : 'montar-entrada-var';
    if (e.seed) {
      variante.append(
        document.createTextNode(
          fixadaDesatualizada(e)
            ? `Variante fixa (${e.seed}) — fixada numa versão anterior do item; a mesma seed agora gera outra questão. `
            : `Variante fixa (${e.seed}). `,
        ),
      );
      const soltar = botao('Soltar', 'app-link');
      soltar.dataset['foco'] = `soltar:${e.id}`;
      soltar.title = 'Passa a gerar uma variante nova a cada percurso';
      soltar.addEventListener('click', () =>
        mudar({
          ...c,
          entradas: c.entradas.map((x) =>
            x.id === e.id ? { id: x.id, templateId: x.templateId } : x,
          ),
        }),
      );
      const ver = botao('Ver', 'app-link');
      ver.addEventListener('click', () => ctx.abrirNoLaboratorio(e.templateId, e.seed));
      variante.append(soltar, document.createTextNode(' · '), ver);
    } else {
      variante.textContent = 'Variante nova a cada percurso';
    }
    corpo.append(titulo, variante);

    const acoes = document.createElement('div');
    acoes.className = 'montar-entrada-acoes';
    const subir = botao('↑', 'app-icone');
    subir.dataset['foco'] = `subir:${e.id}`;
    subir.title = 'Subir';
    subir.setAttribute('aria-label', `Subir ${t.title}`);
    subir.disabled = i === 0;
    subir.addEventListener('click', () => mudar({ ...c, entradas: mover(c.entradas, i, i - 1) }));
    const descer = botao('↓', 'app-icone');
    descer.dataset['foco'] = `descer:${e.id}`;
    descer.title = 'Descer';
    descer.setAttribute('aria-label', `Descer ${t.title}`);
    descer.disabled = i === c.entradas.length - 1;
    descer.addEventListener('click', () => mudar({ ...c, entradas: mover(c.entradas, i, i + 1) }));
    const tirar = botao('✕', 'app-icone');
    tirar.dataset['foco'] = `tirar:${e.id}`;
    tirar.title = 'Tirar do percurso';
    tirar.setAttribute('aria-label', `Tirar ${t.title} do percurso`);
    tirar.addEventListener('click', () =>
      mudar({ ...c, entradas: c.entradas.filter((x) => x.id !== e.id) }),
    );
    acoes.append(subir, descer, tirar);

    li.append(num, corpo, acoes);
    return li;
  }

  window.addEventListener(EVENTO_MUDOU, () => {
    if (!root.hidden) desenhar();
  });

  return { element: root, mostrar: desenhar };
}

// ---------------------------------------------------------------------------

function mover<T>(xs: readonly T[], de: number, para: number): T[] {
  const out = [...xs];
  const [x] = out.splice(de, 1);
  if (x !== undefined) out.splice(para, 0, x);
  return out;
}

function painel(titulo: string): HTMLElement {
  const p = document.createElement('div');
  p.className = 'montar-painel';
  const h = document.createElement('h2');
  h.textContent = titulo;
  p.appendChild(h);
  return p;
}

function chipNivel(nivel: string): HTMLElement {
  const c = document.createElement('span');
  c.className = 'chip chip-nivel';
  c.textContent = nivel;
  return c;
}

function texto<K extends keyof HTMLElementTagNameMap>(tag: K, s: string): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  el.textContent = s;
  return el;
}

function botao(rotulo: string, cls: string): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = cls;
  b.textContent = rotulo;
  return b;
}
